import { Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../auth/auth.service';
import { PermissionsService } from '../auth/permissions.service';
import { CommonService } from '../shared/common.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { LAST_VERSION } from '../shared/common.enums';
import { AppVersionService } from '../shared/app-version.service';
import { HomeUserToolComponent } from './home-user-tool/home-user-tool.component';
import { PURCHASE_MODULE_PRELOAD_PERMISSIONS } from '../shared/routing/preload-permissions';
import { BootCoordinatorService } from '../shared/boot/boot-coordinator.service';

@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [MessageService]
})
export class HomeComponent implements OnInit {
  private readonly sidebarCompactStorageKey = 'sbestSidebarCompact';
  private readonly mobileBreakpoint = 992;
  private readonly moduleNavigationOrder = [
    { access: 'panel', route: '/panel-operativo' },
    { access: 'arqueo', route: '/arqueo' },
    { access: 'rtm', route: '/agenda' },
    { access: 'history', route: '/historial' },
    { access: 'users', route: '/usuarios' },
    { access: 'admin', route: '/administracion' },
    { access: 'finanzas', route: '/finanzas' },
    { access: 'prices', route: '/precios' },
    { access: 'sales', route: '/ventas' },
    { access: 'inventory', route: '/inventario' },
    { access: 'inventoryCatalog', route: '/inventario-consulta' },
    { access: 'salesbinder', route: '/sincronizacion-salesbinder' }
  ];

  private authService = inject(AuthService);
  private perms = inject(PermissionsService);
  get canViewCommissionDemo(): boolean { return this.perms.can('administracion:reportes_pagos'); }
  private router = inject(Router);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  private bootCoordinator = inject(BootCoordinatorService);
  public commonService = inject(CommonService);
  public appVersionService = inject(AppVersionService);

  @ViewChild(HomeUserToolComponent) userToolComponent!: HomeUserToolComponent;

  readonly latestVersion = LAST_VERSION;
  dashboardIcon = 'speed';
  usersIcon = 'manage_accounts';
  historyIcon = 'history';
  arqueoIcon = 'balance';
  rtmIcon = 'route';
  tagsIcon = 'sell';
  adminIcon = 'account_tree';
  ventasIcon = 'point_of_sale';
  pricesIcon = 'swap_horiz';
  invetoryIcon = 'inventory_2';
  comprasIcon = 'local_shipping';
  visualIcon = 'photo_camera';
  salesBinderIcon = 'sync';
  fullSize = false;
  isSidebarCompact = false;
  menuSearchTerm = '';
  sidenavMode: 'side' | 'over' = 'side';
  mobileSidenavOpened = false;
  isImmersiveMobileRoute = false;

  modulesAccess = {
    admin: false,
    finanzas: false,
    panel: false,
    arqueo: false,
    rtm: false,
    users: false,
    visual: false,
    history: false,
    tags: false,
    prices: false,
    sales: false,
    inventory: false,
    inventoryCatalog: false,
    purchases: false,
    salesbinder: false,
  };

  ngOnInit(): void {
    this.commonService.currentStore$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (store) => {
        if (this.commonService.logos[store]) {
          this.commonService.currentLogo = this.commonService.logos[store];
        }
      }
    });

    this.setSidenavMode();
    this.restoreSidebarPreference();
    this.syncImmersiveMobileRoute();
    this.setModulesAccess();
    this.commonService.messageServiceRef = this.messageService;

    if (this.authService.loggedIn) {
      if (this.router.url === "/") {
        this.navigateToFirstAvailableModule();
      }
    } else {
      this.router.navigate(["/login"]);
    }

    this.commonService.fullSize$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (fullSize) => {
        this.fullSize = fullSize;
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.closeMobileSidenav();
        this.syncImmersiveMobileRoute();
      }
    });

    this.bootCoordinator.markFirstAuthorizedRouteReady();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setSidenavMode();
    this.syncImmersiveMobileRoute();
  }

  onSidenavOpenedChange(opened: boolean): void {
    if (this.sidenavMode === 'over') {
      this.mobileSidenavOpened = opened;
      return;
    }

    const nextFullSize = !opened;
    if (this.commonService.fullSize$.value !== nextFullSize) {
      this.commonService.setFullSize(nextFullSize);
    }
  }

  toggleMobileSidenav(): void {
    if (this.sidenavMode === 'over') {
      this.mobileSidenavOpened = !this.mobileSidenavOpened;
    }
  }

  closeMobileSidenav(): void {
    if (this.sidenavMode === 'over') {
      this.mobileSidenavOpened = false;
    }
  }

  toggleSidebarCompact(): void {
    if (this.sidenavMode !== 'side') {
      return;
    }

    this.isSidebarCompact = !this.isSidebarCompact;
    this.menuSearchTerm = '';
    this.persistSidebarPreference();
  }

  updateMenuSearch(event: Event): void {
    this.menuSearchTerm = (event.target as HTMLInputElement).value;
  }

  isMenuItemVisible(label: string): boolean {
    const searchTerm = this.normalizeMenuText(this.menuSearchTerm.trim());
    return !searchTerm || this.normalizeMenuText(label).includes(searchTerm);
  }

  private setSidenavMode(): void {
    const nextMode = window.innerWidth < this.mobileBreakpoint ? 'over' : 'side';
    if (this.sidenavMode !== nextMode) {
      this.sidenavMode = nextMode;
      this.mobileSidenavOpened = false;
    }
  }

  private restoreSidebarPreference(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      this.isSidebarCompact = window.localStorage.getItem(this.sidebarCompactStorageKey) === 'true';
    } catch {
      this.isSidebarCompact = false;
    }
  }

  private persistSidebarPreference(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.sidebarCompactStorageKey, String(this.isSidebarCompact));
    } catch {
      // La preferencia visual no debe interrumpir la navegación si el storage está bloqueado.
    }
  }

  private normalizeMenuText(value: string): string {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private syncImmersiveMobileRoute(): void {
    this.isImmersiveMobileRoute = this.commonService.isMobileViewport(this.mobileBreakpoint)
      && this.router.url.startsWith('/mensajes');
  }

  private setModulesAccess() {
    this.modulesAccess.panel = this.perms.can('panel:view');
    this.modulesAccess.arqueo = this.perms.canAny(['arqueo:sales:view', 'arqueo:saf:view', 'arqueo:clients:view', 'arqueo:fondo:view']);
    this.modulesAccess.rtm = this.perms.canAny(['agenda:view', 'agenda:deliveries:view']);
    this.modulesAccess.admin = this.perms.can('administracion:view');
    this.modulesAccess.finanzas = true;
    this.modulesAccess.users = this.perms.can('users:view');
    this.modulesAccess.visual = this.perms.can('visual:view');
    this.modulesAccess.history = this.perms.can('history:view');
    this.modulesAccess.prices = this.perms.can('prices:view');
    this.modulesAccess.sales = this.perms.canAny(['sales:view', 'sales:cierre_bol:view']);
    // Inventario es un módulo de consulta disponible para cualquier usuario autenticado.
    this.modulesAccess.inventory = this.perms.canAny(['inventory:view', 'inventory:no_list:view', 'inventory:physical_count:create', 'inventory:physical_count:close', 'inventory:materials:convert']);
    this.modulesAccess.inventoryCatalog = true;
    this.modulesAccess.purchases = this.perms.canAny([...PURCHASE_MODULE_PRELOAD_PERMISSIONS]);
    this.modulesAccess.salesbinder = this.perms.can('salesbinder:manage');
  }

  private navigateToFirstAvailableModule(): void {
    if (this.authService.user.value == null) {
      this.router.navigate(["/login"]);
      return;
    }
    const firstAvailable = this.moduleNavigationOrder.find(
      module => this.modulesAccess[module.access as keyof typeof this.modulesAccess]
    );
    if (firstAvailable) {
      this.router.navigate([firstAvailable.route]);
    }
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl('/dummy', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri]);
    });
  }
}
